import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Save } from "lucide-react";
import { useAuth } from "../lib/auth";

const ROLES = ["Staff", "Reviewer", "Officer", "Admin"];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [settings, setSettings] = useState({ available_models: [], default_llm_model: "" });
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", designation: "", role: "Staff" });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/roles").then((r) => setRoles(r.data));
    if (isAdmin) api.get("/admin/users").then((r) => setUsers(r.data));
  }, [isAdmin]);

  const saveSettings = async () => {
    try {
      const { data } = await api.patch("/settings", settings);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", full_name: "", designation: "", role: "Staff" });
      setOpen(false);
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout breadcrumbs={["SSRD", "Settings"]}>
      <div className="mb-6">
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Administration</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="settings-title">Settings</h1>
      </div>

      <Tabs defaultValue="users">
        <TabsList className="grid grid-cols-4">
          <TabsTrigger value="users" data-testid="settings-tab-users">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="settings-tab-roles">Roles</TabsTrigger>
          <TabsTrigger value="ai" data-testid="settings-tab-ai">Drafting Engine</TabsTrigger>
          <TabsTrigger value="export" data-testid="settings-tab-export">Export</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card><CardContent className="p-0">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div className="text-lg font-semibold">Users</div>
              {isAdmin && (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild><Button className="gap-2" data-testid="add-user-btn"><Plus className="size-4" /> Add User</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
                    <form onSubmit={createUser} className="space-y-3">
                      <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                      <div><Label className="text-xs">Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
                      <div><Label className="text-xs">Full Name</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                      <div><Label className="text-xs">Designation</Label><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></div>
                      <div>
                        <Label className="text-xs">Role</Label>
                        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <DialogFooter><Button type="submit">Create</Button></DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Email</TableHead>
                <TableHead>Role</TableHead><TableHead>Designation</TableHead>
                <TableHead>Status</TableHead><TableHead>Last login</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {!isAdmin && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">Only admins can list users.</TableCell></TableRow>}
                {isAdmin && users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No users.</TableCell></TableRow>}
                {users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="text-xs">{u.full_name}</TableCell>
                    <TableCell className="text-xs font-mono">{u.email}</TableCell>
                    <TableCell className="text-xs">{u.role}</TableCell>
                    <TableCell className="text-xs">{u.designation}</TableCell>
                    <TableCell className="text-xs">{u.user_status}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{(u.last_login_at || "—").toString().slice(0, 16).replace("T", " ")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="roles">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Role</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {roles.map((r) => (
                  <TableRow key={r.role_id}>
                    <TableCell className="font-semibold">{r.role_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.role_description}</TableCell>
                    <TableCell className="text-xs">{r.role_status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ai">
          <Card><CardContent className="p-5 space-y-4 max-w-xl">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Remi8 Drafting Engine</div>
            <div>
              <Label className="text-xs">Engine Label (displayed in UI)</Label>
              <Input value={settings.engine_label || ""} onChange={(e) => setSettings({ ...settings, engine_label: e.target.value })} data-testid="settings-engine-label" />
            </div>
            <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs">
              <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Active Engine Build</div>
              <div className="font-mono text-sm">Remi8-128B</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Engine routing is managed by the Remi8 platform.
                Contact the system administrator to change platform configuration.
              </div>
            </div>
            <div>
              <Label className="text-xs">Default OCR Language</Label>
              <Input value={settings.default_ocr_language || ""} onChange={(e) => setSettings({ ...settings, default_ocr_language: e.target.value })} />
            </div>
            {isAdmin && <Button onClick={saveSettings} className="gap-2" data-testid="settings-save-btn"><Save className="size-4" /> Save</Button>}
            {!isAdmin && <div className="text-xs text-amber-700">Only Admins can save settings.</div>}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="export">
          <Card><CardContent className="p-5 max-w-xl">
            <div className="text-[10px] tracking-widest text-muted-foreground uppercase mb-2">Export</div>
            <Label className="text-xs">Available export formats</Label>
            <Input
              value={(settings.available_export_formats || []).join(", ")}
              onChange={(e) => setSettings({ ...settings, available_export_formats: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
            />
            {isAdmin && <Button onClick={saveSettings} className="mt-3 gap-2"><Save className="size-4" /> Save</Button>}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
